/* eslint-disable no-console */
/* eslint-disable max-lines */
import { useEffect } from 'react';

import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Link,
  Text,
} from '@joinrepublic/dls-ui-kit';
import { SuccessfulNativeLogin, UpdateLoginFlowBody } from '@ory/kratos-client';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';

import { CloudflareTurnstile } from '@r-client/shared/feature/captcha';
import { E_REGION } from '@r-client/shared/feature/region';
import { PasswordInput } from '@r-client/shared/ui/chakra-ui-form';
import { setCookie, useAppConfig } from '@r-client/shared/util/core';

import {
  CaptchaVerificationError,
  getSocialAuthSources,
  IJurisdictionAccounts,
  SessionPersistError,
  SocialAuthButton,
  useGetLoginFlowQuery,
  useSubmitLoginMutation,
} from '../';
import { useCaptchaState } from '../hooks/use-captcha-state';
import { getJurisdictionAccountsFromSession } from '../utils/jurisdiction';

interface IUpdateLoginFlowBody extends Omit<UpdateLoginFlowBody, 'method'> {
  method: 'password';
  identifier: string;
  password: string;
}

interface ILoginFormProps {
  onSuccessfulLogin: (
    returnTo?: string,
    accounts?: IJurisdictionAccounts,
    globalLogin?: SuccessfulNativeLogin,
    proxyResponseHeaders?: Record<string, string>
  ) => unknown | Promise<unknown>;
  onRequiresVerification: (email: string, verificationFlowId: string) => void;
  isSignedInGlobal: boolean;
  region: E_REGION;
  returnTo?: string;
  /** Shows the "social account isn't linked" error after a rejected social login. */
  socialLoginRejected?: boolean;
  /** Ory Hydra OIDC login challenge, threaded into the login flow to continue the OIDC handshake. */
  loginChallenge?: string;
  /**
   * Existing Kratos flow to continue, from the `?flow=` Kratos puts on the URL when it redirects
   * here. For an OIDC login that flow is the one holding `oauth2_login_challenge`, so continuing it
   * rather than creating a new one is what completes the Hydra handshake on submit.
   */
  flowId?: string;
}

const SOCIAL_NO_ACCOUNT_ERROR_MESSAGE =
  "We couldn't find a Republic account linked to that social login. Please log in with your email and password.";

export const LoginForm = observer(function LoginForm({
  onSuccessfulLogin,
  onRequiresVerification,
  isSignedInGlobal,
  region,
  returnTo,
  socialLoginRejected = false,
  loginChallenge,
  flowId,
}: ILoginFormProps) {
  const { publicTld } = useAppConfig();
  const router = useRouter();
  const form = useForm<IUpdateLoginFlowBody>({ mode: 'onTouched' });

  const flowQuery = useGetLoginFlowQuery({
    flowId,
    returnTo,
    refresh: isSignedInGlobal,
    loginChallenge,
  });
  const loginMutation = useSubmitLoginMutation();

  const {
    captchaRef,
    captchaResetNonce,
    captchaState,
    captchaStateRef,
    isSubmittingRef,
    toast,
    onCaptchaSuccess,
    onCaptchaBeforeInteractive,
    onCaptchaError,
    renewCaptchaPromise,
    refreshCaptchaToken,
  } = useCaptchaState({
    form,
    region,
    returnTo,
    flowAction: flowQuery.data?.ui?.action,
  });

  // isLoading covers the mutation AND the time spent awaiting background deps on submit.
  const isLoading = loginMutation.isPending || form.formState.isSubmitting;
  const successfullyLoggedIn =
    loginMutation.isSuccess && loginMutation.data.success;

  // Surface the "social account not linked" error (after a rejected social login)
  // as a dismissable form error, so it clears as soon as the user starts typing.
  useEffect(() => {
    if (socialLoginRejected) {
      form.setError('root', { message: SOCIAL_NO_ACCOUNT_ERROR_MESSAGE });
    }
  }, [socialLoginRejected, form]);

  const handleSubmit = async (data: IUpdateLoginFlowBody) => {
    if (captchaStateRef.current === 'interactive') {
      return form.setError('root', {
        type: 'manual',
        message:
          'Additional verification is required, please complete the captcha',
      });
    }

    // If the captcha has failed, we need to refresh it before submitting the form again. This allows
    // the user to attempt to complete the captcha again and get a new token.
    if (captchaStateRef.current === 'failed') {
      refreshCaptchaToken();
    }

    isSubmittingRef.current = true;
    try {
      const [activeFlow, activeCaptchaToken] = await Promise.all([
        flowQuery.data ??
          flowQuery.refetch({ cancelRefetch: false }).then((r) => {
            if (!r.data) throw new Error('Failed to load login flow');
            return r.data;
          }),
        captchaRef.current.promise,
      ]);

      // Ensure region cookie matches the target region
      setCookie({
        name: 'region',
        value: region,
        options: {
          maxAge: 60 * 60 * 24 * 400,
          sameSite: 'lax',
          domain: publicTld,
        },
      });

      const result = await loginMutation.mutateAsync({
        ...data,
        flow: activeFlow,
        transient_payload: {
          captcha_token: activeCaptchaToken,
        },
      });

      if (result.success) {
        const accounts = result.globalLogin
          ? getJurisdictionAccountsFromSession(result.globalLogin.session)
          : undefined;
        await onSuccessfulLogin(
          result.redirectTo,
          accounts,
          result.globalLogin,
          result.proxyResponseHeaders
        );
      } else if (result.requiresVerification) {
        onRequiresVerification(result.email, result.verificationFlowId);
      } else if ('errors' in result) {
        refreshCaptchaToken();
        result.errors.forEach((error) => {
          form.setError(error.field || 'root', {
            message: error.message,
          });
        });
      }
    } catch (error) {
      if (error instanceof CaptchaVerificationError) {
        toast({
          title: 'Captcha verification failed',
          description: error.message,
          status: 'error',
          duration: 10000,
          isClosable: true,
        });
        return;
      }

      form.setError('root', {
        type:
          error instanceof SessionPersistError ? 'session-persist' : 'manual',
        message:
          error instanceof Error
            ? error.message
            : 'An error occurred while attempting to log in. Please try again later.',
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const queryString = router.asPath.split('?')[1] ?? '';
  const forgotPasswordUrl = `/auth/forgot-password?${queryString}`;

  return (
    <div>
      <form
        aria-label="Login form"
        action={flowQuery.data?.ui?.action}
        method={flowQuery.data?.ui?.method}
        onSubmit={form.handleSubmit(handleSubmit)}
        noValidate
      >
        <FormControl
          id="identifier"
          mb="6"
          isInvalid={!!form.formState.errors.identifier}
        >
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            placeholder="Email"
            aria-label="Email"
            height="12"
            px="4"
            py="5"
            borderColor="grey.400"
            _placeholder={{ color: 'grey.600' }}
            {...form.register('identifier', {
              required: "Email can't be blank",
              onChange: () => form.clearErrors('root'),
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: 'Please enter a valid email address',
              },
            })}
          />
          <FormErrorMessage>
            {form.formState.errors.identifier?.message}
          </FormErrorMessage>
        </FormControl>

        <FormControl
          id="password"
          mb="5"
          isInvalid={!!form.formState.errors.password}
        >
          <PasswordInput
            inputProps={{
              autoComplete: 'current-password',
              placeholder: 'Password (min 8 characters)',
              variant: undefined,
              fontSize: 'md',
              'aria-label': 'Password',
              height: '48px',
              px: '4',
              py: '5',
              borderColor: 'grey.400',
              _placeholder: { color: 'text.secondary' },
              ...form.register('password', {
                required: "Password can't be blank",
                onChange: () => form.clearErrors('root'),
              }),
            }}
          />
          <FormErrorMessage>
            {form.formState.errors.password?.message}
          </FormErrorMessage>
        </FormControl>

        <Input type="hidden" {...form.register('method')} value="password" />

        <FormControl
          isInvalid={!!form.formState.errors.root || flowQuery.isError}
          mb="6"
        >
          <FormErrorMessage flexDirection="column" alignItems="start" gap="1">
            {form.formState.errors.root?.type === 'session-persist' ? (
              <>
                <Box fontWeight="semibold">
                  You signed in, but your session couldn&apos;t be saved.
                </Box>
                <Box as="ul" pl="4" listStyleType="disc">
                  <li>
                    Try opening Republic in a{' '}
                    <strong>private/incognito window</strong>
                  </li>
                  <li>Disable any VPN, proxy, or corporate network filter</li>
                  <li>Pause ad blockers or browser extensions</li>
                </Box>
                <Box>
                  Still not working?{' '}
                  <Link
                    href="mailto:team@republic.co"
                    sx={{
                      textDecoration: 'underline',
                      fontSize: 'inherit',
                      color: 'inherit',
                    }}
                  >
                    Contact team@republic.co
                  </Link>
                </Box>
              </>
            ) : (
              form.formState.errors.root?.message ||
              'An unexpected error occurred. Please try again or contact support.'
            )}
          </FormErrorMessage>
        </FormControl>

        <Link
          href={forgotPasswordUrl}
          color="grey.700"
          fontWeight="normal"
          textDecoration="underline"
          mb="6"
          display="block"
          maxWidth="max-content"
          size="body-md"
        >
          Forgot password?
        </Link>

        <CloudflareTurnstile
          action="login"
          onSuccess={onCaptchaSuccess}
          onBeforeInteractive={onCaptchaBeforeInteractive}
          onExpire={renewCaptchaPromise}
          resetNonce={captchaResetNonce}
          onError={onCaptchaError}
        />

        <Button
          variant="primary"
          width="100%"
          type="submit"
          isLoading={isLoading}
          isDisabled={
            !form.formState.isValid ||
            isLoading ||
            flowQuery.isError ||
            successfullyLoggedIn ||
            captchaState === 'interactive'
          }
          height="12"
        >
          {successfullyLoggedIn ? 'Redirecting...' : 'Log in'}
        </Button>
      </form>

      <Box position="relative" my="8">
        <Divider color="grey.100" />
        <Text
          position="absolute"
          left="50%"
          top="50%"
          transform="translate(-50%, -50%)"
          bg="white"
          px="4"
          color="grey.500"
          size="body-sm"
        >
          Or log in with
        </Text>
      </Box>

      <form
        id="social-auth-form"
        action={flowQuery.data?.ui?.action}
        method={flowQuery.data?.ui?.method}
      ></form>
      <Flex gap="4" alignItems="center" justifyContent="center">
        {Object.values(getSocialAuthSources(region)).map((source) => (
          <SocialAuthButton
            key={source.displayName}
            socialAuthSource={source}
            disabled={isLoading}
          />
        ))}
      </Flex>
    </div>
  );
});
